import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Loader2, RefreshCcw, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../supabase';
import { SALE_STATUS_REVISION } from '../utils/mpPoint';

/**
 * Bandeja de cobros a verificar.
 *
 * Una venta llega acá cuando el POS mandó un cobro a la terminal Point, el
 * cajero canceló (o expiró el tiempo) y Mercado Pago NO confirmó la cancelación.
 * En ese caso no se puede borrar la venta —el cliente pudo haber pasado la
 * tarjeta igual— pero tampoco contarla como ingreso, así que queda en
 * 'cobro_en_revision' (estado excluido en flujoFondos.js).
 *
 * Desde acá se consulta el estado real de la orden en Mercado Pago y se resuelve:
 * si se cobró, la venta pasa a Pagado; si no, se anula.
 *
 * No depende de los filtros de fecha del historial a propósito: una venta en
 * revisión de hace tres días tiene que verse aunque el filtro esté en "hoy".
 */
export default function CobrosAVerificar({ organizationId, onResolved }) {
  const [ventas, setVentas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [resolviendo, setResolviendo] = useState(null);
  const [abierto, setAbierto] = useState(true);
  // Resultado de la última consulta a Mercado Pago por venta, para que el cajero
  // vea qué pasó sin volver a consultar.
  const [estados, setEstados] = useState({});

  const cargar = useCallback(async () => {
    if (!organizationId) return;
    setCargando(true);
    try {
      const { data, error } = await supabase
        .from('sales')
        .select('id, total, created_at, status, sale_gateway_payments(gateway_order_id, status, provider, mode)')
        .eq('organization_id', organizationId)
        .eq('status', SALE_STATUS_REVISION)
        .eq('active', true)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      setVentas(data || []);
    } catch (err) {
      console.error('Error cargando cobros a verificar:', err);
    } finally {
      setCargando(false);
    }
  }, [organizationId]);

  useEffect(() => { cargar(); }, [cargar]);

  const consultarEnMp = async (venta) => {
    const orderId = venta?.sale_gateway_payments?.[0]?.gateway_order_id;
    if (!orderId) {
      toast.error('Esta venta no tiene orden de Mercado Pago registrada. Resolvela a mano.');
      return;
    }
    setResolviendo(venta.id);
    try {
      const { data, error } = await supabase.functions.invoke('mp-point-order', {
        body: { organizationId, action: 'status', orderId },
      });
      if (error || data?.error) throw new Error(error?.message || data?.error);

      setEstados((prev) => ({ ...prev, [venta.id]: { status: data.status, paid: data.paid } }));

      if (data.paid) {
        // NO la marcamos "Pagado" desde acá: esta venta nunca descontó stock ni
        // asentó caja/diario (el POS corta antes). Marcarla pagada crearía un
        // ingreso sin respaldo. Hay que anularla y rehacerla en el POS.
        toast(
          'El cobro SÍ entró en la terminal. Anulá esta venta y volvé a registrarla en el POS con el cobro ya hecho.',
          { icon: '💳', duration: 12000 },
        );
      } else {
        toast(`La orden está en estado "${data.status}": no se cobró. Podés anular la venta.`, {
          icon: 'ℹ️', duration: 8000,
        });
      }
    } catch (err) {
      console.error('Error consultando la orden en MP:', err);
      toast.error(`No se pudo consultar Mercado Pago: ${err.message}`);
    } finally {
      setResolviendo(null);
    }
  };

  /**
   * Anula la venta en revisión. No revierte stock ni asientos a propósito: esta
   * venta nunca los aplicó (el POS corta antes), así que revertir inflaría el
   * inventario. Ver annulSaleWithReversal, que contempla este estado.
   */
  const anular = async (venta, mensaje) => {
    setResolviendo(venta.id);
    try {
      const { error } = await supabase
        .from('sales')
        .update({ active: false, status: 'anulada', invoice_status: 'anulada' })
        .eq('id', venta.id)
        .eq('organization_id', organizationId);
      if (error) throw error;
      toast.success(mensaje, { duration: 8000 });
      setVentas((prev) => prev.filter((v) => v.id !== venta.id));
      if (typeof onResolved === 'function') onResolved();
    } catch (err) {
      console.error('Error anulando la venta en revisión:', err);
      toast.error(`No se pudo anular la venta: ${err.message}`);
    } finally {
      setResolviendo(null);
    }
  };

  if (!ventas.length) return null;

  return (
    <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <span className="flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-600" />
          <span className="text-[11px] font-black uppercase tracking-widest text-amber-800">
            {ventas.length} {ventas.length === 1 ? 'cobro a verificar' : 'cobros a verificar'}
          </span>
        </span>
        <span className="text-[9px] font-black uppercase tracking-widest text-amber-600">
          {abierto ? 'Ocultar' : 'Ver'}
        </span>
      </button>

      {abierto && (
        <>
          <p className="mt-2 text-[10px] font-bold leading-relaxed text-amber-700">
            No se pudo confirmar la cancelación del cobro en la terminal. Estas ventas
            no cuentan como ingreso ni descontaron stock. Consultá el estado en Mercado Pago:
            si no se cobró, anulalas; si se cobró, anulalas igual y registrá la venta de nuevo
            en el POS con el cobro ya hecho.
          </p>

          <div className="mt-3 space-y-2">
            {ventas.map((venta) => {
              const pago = venta?.sale_gateway_payments?.[0] || null;
              const ocupado = resolviendo === venta.id;
              const estado = estados[venta.id] || null;
              return (
                <div
                  key={venta.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-white px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-700">
                      #{String(venta.id).slice(0, 8).toUpperCase()} · $ {Number(venta.total || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                    </p>
                    <p className="truncate text-[9px] font-bold text-slate-400">
                      {new Date(venta.created_at).toLocaleString('es-AR')}
                      {pago?.gateway_order_id ? ` · Orden ${pago.gateway_order_id}` : ' · Sin orden registrada'}
                    </p>
                    {estado && (
                      <p className={`mt-0.5 text-[9px] font-black uppercase tracking-widest ${estado.paid ? 'text-rose-600' : 'text-emerald-600'}`}>
                        {estado.paid
                          ? '💳 Se cobró en la terminal — anulá y rehacé la venta en el POS'
                          : `No se cobró (${estado.status})`}
                      </p>
                    )}
                  </div>

                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      disabled={ocupado}
                      onClick={() => consultarEnMp(venta)}
                      className="flex items-center gap-1 rounded-lg bg-sky-500 px-2.5 py-1.5 text-[9px] font-black uppercase tracking-widest text-white transition-all hover:bg-sky-600 disabled:opacity-40"
                    >
                      {ocupado ? <Loader2 size={12} className="animate-spin" /> : <RefreshCcw size={12} />}
                      Consultar
                    </button>
                    <button
                      type="button"
                      disabled={ocupado}
                      onClick={() => anular(
                        venta,
                        estado?.paid
                          ? 'Venta anulada. El cobro ya está en Mercado Pago: registrá la venta de nuevo en el POS con el método que corresponda.'
                          : 'Venta anulada.',
                      )}
                      title="Anular la venta en revisión (no revierte stock ni caja: nunca se aplicaron)"
                      className="flex items-center gap-1 rounded-lg bg-slate-200 px-2.5 py-1.5 text-[9px] font-black uppercase tracking-widest text-slate-600 transition-all hover:bg-slate-300 disabled:opacity-40"
                    >
                      <X size={12} /> Anular
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {cargando && (
            <p className="mt-2 text-[9px] font-bold uppercase tracking-widest text-amber-600">Actualizando…</p>
          )}
        </>
      )}
    </div>
  );
}
